import * as Sentry from "@sentry/react";
import { createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { config } from "./config";
import { AuthProvider } from "./context/auth-provider";
import { GraphqlProvider } from "./context/graphql-provider";
import { RouterProvider } from "./context/router-provider";
import { ThemeProvider } from "./context/theme-provider";
import { NotFoundError } from "./features/errors/not-found-error";
import { UnknownError } from "./features/errors/unknown-error";
import { routeTree } from "./routeTree.gen";
import "./index.css";

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Create a new router instance
export const router = createRouter({
  defaultNotFoundComponent: NotFoundError,
  defaultErrorComponent: UnknownError,
  notFoundMode: "fuzzy",
  routeTree,
  context: {
    userId: "",
    urqlClient: undefined!,
  },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
});

if (config.SENTRY_DSN) {
  Sentry.init({
    dsn: config.SENTRY_DSN,
    environment: __APP_STAGE__,
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.replayIntegration(),
    ],
    release: `copper-web@${__APP_VERSION__}`,
    // Capture Replay for 10% of all sessions, plus for 100% of sessions with an error
    replaysOnErrorSampleRate: 1,
    replaysSessionSampleRate: 0.1,
    // Set tracesSampleRate to .2 to capture 20% of transactions for tracing in production.
    tracesSampleRate: __APP_STAGE__ === "production" ? 0.2 : 1,
  });
}

// Render the app
const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
      console.warn("Uncaught error", error, errorInfo.componentStack);
    }),

    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),

    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
  });

  root.render(
    <StrictMode>
      <AuthProvider router={router}>
        <GraphqlProvider router={router}>
          <ThemeProvider>
            <RouterProvider router={router} />
          </ThemeProvider>
        </GraphqlProvider>
      </AuthProvider>
    </StrictMode>,
  );
}
